/**
 * Scripts API
 *
 * Handles all script-related API calls (Scripts, Steps, Phrases).
 * Uses Zod validation from @aica/api-types (SSOT).
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  scriptsListResponseSchema,
  scriptResponseSchema,
  scriptWithStepsResponseSchema,
  scriptStepResponseSchema,
  scriptStepCreatedResponseSchema,
  scriptPhraseResponseSchema,
  successResponseSchema,
  type ScriptsListResponse,
  type ScriptResponse,
  type ScriptWithStepsResponse,
  type ScriptStepResponse,
  type ScriptStepCreatedResponse,
  type ScriptPhraseResponse,
  type ScriptParseRequest,
  type ScriptParseResult,
} from '@aica/api-types/schemas';
import type { SuccessResponse } from '../types';

export type ParseScriptRequest = ScriptParseRequest;
export interface ParseScriptResponse {
  data: ScriptParseResult;
}

export interface ProjectScript {
  id: string;
  scriptId: string;
  projectId: string;
  isDefault: boolean;
  name: string;
  category: string;
}

export class ScriptsApi extends BaseApiClient {
  // ============================================================================
  // PROJECT SCRIPTS (assignment to project)
  // ============================================================================

  async getProjectScripts(
    projectId: string
  ): Promise<{ scripts: ProjectScript[]; total: number }> {
    return this.request<{ scripts: ProjectScript[]; total: number }>(
      API_ENDPOINTS.PROJECT_SCRIPTS(projectId)
    );
  }

  async assignToProject(
    projectId: string,
    scriptId: string,
    isDefault?: boolean
  ): Promise<{ projectScript: ProjectScript }> {
    return this.request<{ projectScript: ProjectScript }>(
      API_ENDPOINTS.PROJECT_SCRIPTS(projectId),
      {
        method: 'POST',
        body: JSON.stringify({ scriptId, isDefault: isDefault ?? true }),
      }
    );
  }

  async unassignFromProject(
    projectId: string,
    scriptId: string
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(
      API_ENDPOINTS.PROJECT_SCRIPTS_BY_ID(projectId, scriptId),
      { method: 'DELETE' }
    );
  }

  async setDefaultScript(projectId: string, scriptId: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(
      API_ENDPOINTS.PROJECT_SCRIPTS_DEFAULT(projectId, scriptId),
      { method: 'PATCH' }
    );
  }

  // ============================================================================
  // AI SCRIPT PARSER
  // ============================================================================

  async parseScript(data: ParseScriptRequest): Promise<ParseScriptResponse> {
    return this.request<ParseScriptResponse>(API_ENDPOINTS.SCRIPTS_PARSE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // SCRIPTS
  // ============================================================================

  async getScripts(): Promise<ScriptsListResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS,
      scriptsListResponseSchema
    ) as Promise<ScriptsListResponse>;
  }

  async getScriptById(id: string): Promise<ScriptWithStepsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_BY_ID(id),
      scriptWithStepsResponseSchema
    ) as Promise<ScriptWithStepsResponse>;
  }

  async createScript(data: {
    name: string;
    description?: string;
    category: string;
    isActive?: boolean;
    /** null unbinds (default), a value binds this script to a call category / chat. */
    binding?: string | null;
    /**
     * Шаги от AI-разбора, вложенные в тот же запрос — POST /api/v1/scripts
     * принимает их и создаёт ОДНИМ ЗАПРОСОМ вместе со скриптом (тот же путь,
     * что и «Дублировать скрипт»). Это НЕ атомарно на уровне хранилища:
     * `ScriptRepository.create()` вставляет скрипт, затем шаги циклом без
     * транзакции — отказ на середине оставляет полускрипт (issue #505).
     * См. `CreateScriptForm.steps` (#496).
     */
    steps?: Array<{
      name: string;
      description?: string;
      isRequired: boolean;
      orderEnforced: boolean;
      weight: number;
      phrases?: Array<{
        phraseText: string;
        phraseType: 'required' | 'optional' | 'forbidden';
        matchType: 'exact' | 'semantic' | 'regex';
        semanticThreshold?: number;
        weight: number;
      }>;
    }>;
  }): Promise<ScriptResponse> {
    return this.validatedRequest(API_ENDPOINTS.SCRIPTS, scriptResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ ...data, isActive: data.isActive ?? true }),
    }) as Promise<ScriptResponse>;
  }

  async updateScript(
    id: string,
    data: {
      name?: string;
      description?: string;
      category?: string;
      isActive?: boolean;
      /** null unbinds, a value rebinds (409 if the binding is already in use). */
      binding?: string | null;
    }
  ): Promise<ScriptResponse> {
    return this.validatedRequest(API_ENDPOINTS.SCRIPTS_BY_ID(id), scriptResponseSchema, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<ScriptResponse>;
  }

  async deleteScript(id: string): Promise<SuccessResponse> {
    return this.validatedRequest(API_ENDPOINTS.SCRIPTS_BY_ID(id), successResponseSchema, {
      method: 'DELETE',
    }) as Promise<SuccessResponse>;
  }

  /**
   * Clone an existing script (steps + phrases) under a new id.
   * Backend returns the new script wrapped in `{ data: Script }` at
   * POST /api/v1/scripts/:id/duplicate — same envelope as create/get,
   * so we reuse scriptResponseSchema directly.
   */
  async duplicateScript(id: string): Promise<ScriptResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_DUPLICATE(id),
      scriptResponseSchema,
      {
        method: 'POST',
      }
    ) as Promise<ScriptResponse>;
  }

  // ============================================================================
  // SCRIPT STEPS
  // ============================================================================

  async createScriptStep(
    scriptId: string,
    data: {
      name: string;
      description?: string;
      isRequired: boolean;
      orderEnforced: boolean;
      weight: number;
      /** Created with the step in one request — how an undone delete puts it back. */
      phrases?: Array<{
        phraseText: string;
        phraseType: 'required' | 'optional' | 'forbidden';
        matchType: 'exact' | 'semantic' | 'regex';
        semanticThreshold?: number;
        weight: number;
      }>;
    }
  ): Promise<ScriptStepCreatedResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_STEPS(scriptId),
      scriptStepCreatedResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ) as Promise<ScriptStepCreatedResponse>;
  }

  async updateScriptStep(
    scriptId: string,
    stepId: string,
    data: {
      name?: string;
      description?: string;
      isRequired?: boolean;
      orderEnforced?: boolean;
      weight?: number;
    }
  ): Promise<ScriptStepResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_STEPS_BY_ID(scriptId, stepId),
      scriptStepResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ) as Promise<ScriptStepResponse>;
  }

  async deleteScriptStep(scriptId: string, stepId: string): Promise<SuccessResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_STEPS_BY_ID(scriptId, stepId),
      successResponseSchema,
      {
        method: 'DELETE',
        // Sent by the step editor after an undo window or a pause, and also
        // as the tab closes (#2903): `keepalive` lets the browser finish a
        // request already on its way when the page goes.
        keepalive: true,
      }
    ) as Promise<SuccessResponse>;
  }

  async reorderScriptSteps(
    scriptId: string,
    stepIds: string[]
  ): Promise<SuccessResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_STEPS_REORDER(scriptId),
      successResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify({ stepIds }),
        // Sent by the step editor after an undo window or a pause, and also
        // as the tab closes (#2903): `keepalive` lets the browser finish a
        // request already on its way when the page goes.
        keepalive: true,
      }
    ) as Promise<SuccessResponse>;
  }

  // ============================================================================
  // SCRIPT PHRASES
  // ============================================================================

  async createScriptPhrase(
    scriptId: string,
    stepId: string,
    data: {
      phraseText: string;
      phraseType: 'required' | 'optional' | 'forbidden';
      matchType: 'exact' | 'semantic' | 'regex';
      semanticThreshold?: number;
      weight: number;
    }
  ): Promise<ScriptPhraseResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_PHRASES(scriptId, stepId),
      scriptPhraseResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ) as Promise<ScriptPhraseResponse>;
  }

  async updateScriptPhrase(
    scriptId: string,
    stepId: string,
    phraseId: string,
    data: {
      phraseText?: string;
      phraseType?: 'required' | 'optional' | 'forbidden';
      matchType?: 'exact' | 'semantic' | 'regex';
      semanticThreshold?: number;
      weight?: number;
    }
  ): Promise<ScriptPhraseResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_PHRASES_BY_ID(scriptId, stepId, phraseId),
      scriptPhraseResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ) as Promise<ScriptPhraseResponse>;
  }

  async deleteScriptPhrase(
    scriptId: string,
    stepId: string,
    phraseId: string
  ): Promise<SuccessResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SCRIPTS_PHRASES_BY_ID(scriptId, stepId, phraseId),
      successResponseSchema,
      {
        method: 'DELETE',
        // Sent by the step editor after an undo window or a pause, and also
        // as the tab closes (#2903): `keepalive` lets the browser finish a
        // request already on its way when the page goes.
        keepalive: true,
      }
    ) as Promise<SuccessResponse>;
  }
}
